import Icon from '@atoms/Icon/Icon';
import Image from '@atoms/Image/Image';
import { INavigationCard } from '@models/IHeader';
import IconSizeEnum from '@models/enums/IconSizeEnum';
import useIsInIFrame from '@utils/hooks/useIsInIFrame';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import classNames from 'classnames';
import { AnimatePresence, motion } from 'framer-motion';
import { useState } from 'react';

interface Props extends INavigationCard {
  readonly classes: string;
  readonly onClick: (params: string) => void;
  readonly dataTestId?: string;
}

const NavigationCard = ({
  image,
  link,
  classes,
  onClick,
  dataTestId = 'Navigation-Card',
}: Props) => {
  const { name = '', url, target = '_self' } = link;
  const isInIFrame = useIsInIFrame();
  const isMobile = useMediaQuery(1440);
  const [isHovered, setIsHovered] = useState(false);

  return (
    <a
      href={url}
      onClick={() => onClick(name)}
      target={isInIFrame ? '_parent' : target}
      className="body-two w-full text-center text-black-100 xl:leading-28"
      data-testid={`${dataTestId}`}
      onMouseEnter={() => {
        if (isMobile) return;
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        if (isMobile) return;
        setIsHovered(false);
      }}
    >
      <div className={classNames('flex flex-col items-center', classes)}>
        <Image
          sizes="300px"
          image={image}
          imageClasses="mb-12 w-full h-40 lg:h-56 object-contain"
          dataTestId={`${dataTestId}-Image`}
        />

        <motion.div className="flex w-full justify-center gap-x-[15px]">
          <motion.div
            layout
            className={classNames('max-w-[80%]', { 'font-normal': isHovered })}
          >
            {name}
          </motion.div>

          <AnimatePresence mode="popLayout">
            {isHovered && (
              <motion.div
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                initial={{ opacity: 0 }}
                className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-yellow"
                data-testid={`${dataTestId}-Icon`}
              >
                <Icon name="east" size={IconSizeEnum.sm} />
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </a>
  );
};

export default NavigationCard;
